import React from "react";
import { Box, IconButton, CircularProgress } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { formatHumanMediaBytes } from "../../../../utils/formatHumanMediaBytes";

export default function ChatMediaCollapsedPreview({
  thumbnailUrl,
  fallbackSrc,
  sizeBytes,
  onActivate,
  minHeight = 150,
  maxHeight = 200,
  bottomLeft,
  borderRadius = "10px",
  isLoading = false,
}) {
  const sizeLabel = formatHumanMediaBytes(sizeBytes);

  // Only use local sources as background — never fetch from server while in collapsed state
  const rawSrc = thumbnailUrl || fallbackSrc || null;
  const isLocalSrc =
    rawSrc &&
    (rawSrc.startsWith("blob:") || rawSrc.startsWith("data:"));
  const bgSrc = isLocalSrc ? rawSrc : null;

  return (
    <Box
      sx={{
        position: "relative",
        maxWidth: "calc(var(--chatbox-width, 100vw) * 0.5)",
        width: "100%",
        minHeight,
        maxHeight,
        minWidth: 150,
        borderRadius,
        overflow: "hidden",
        border: (theme) =>
          theme.palette.mode === "dark" ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(255,255,255,0.85)",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "#1a1a1a" : "#d8d8d8",
      }}
    >
      {bgSrc ? (
        <Box
          component="img"
          src={bgSrc}
          alt=""
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: "blur(22px)",
            transform: "scale(1.08)",
            opacity: 0.85,
          }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      ) : (
        // Shimmer skeleton — no server request
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "linear-gradient(90deg, #1e1e1e 25%, #2d2d2d 50%, #1e1e1e 75%)"
                : "linear-gradient(90deg, #e0e0e0 25%, #ebebeb 50%, #e0e0e0 75%)",
            backgroundSize: "200% 100%",
            animation: "skeletonShimmer 1.6s linear infinite",
            "@keyframes skeletonShimmer": {
              from: { backgroundPosition: "200% 0" },
              to: { backgroundPosition: "-200% 0" },
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              opacity: 0.25,
              color: (theme) => (theme.palette.mode === "dark" ? "#fff" : "#000"),
            }}
          >
            <ImageOutlinedIcon sx={{ fontSize: 28 }} />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              pointerEvents: "none",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              borderRadius: "9999px",
              backgroundColor: "rgba(0, 0, 0, 0.52)",
            }}
          >
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          </Box>
        ) : (
          <IconButton
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onActivate?.();
            }}
            sx={{
              pointerEvents: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: "9999px",
              backgroundColor: "rgba(0, 0, 0, 0.52)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.62)" },
            }}
            aria-label={sizeLabel ? `Download or open, ${sizeLabel}` : "Download or open media"}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 22 }} />
            {sizeLabel ? (
              <Box component="span" sx={{ fontSize: "0.875rem", fontWeight: 600, letterSpacing: "0.02em" }}>
                {sizeLabel}
              </Box>
            ) : null}
          </IconButton>
        )}
      </Box>

      {bottomLeft ? (
        <Box
          sx={{
            position: "absolute",
            left: 8,
            bottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: "#fff",
            textShadow: "0 1px 2px rgba(0,0,0,0.75)",
            fontSize: "0.75rem",
            pointerEvents: "none",
          }}
        >
          {bottomLeft}
        </Box>
      ) : null}
    </Box>
  );
}
